import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * Renders the article's `content` string safely.
 * Trusted source (admin-authored). Supports basic HTML (<b>, <i>, <a>, <ul>, <li>, <p>).
 * If content lacks tags, we render each blank-line-separated block as its own <p>.
 */
const renderBody = (raw) => {
  if (!raw) return null;
  const text = String(raw).trim();
  const hasHtml = /<\/?[a-z][\s\S]*>/i.test(text);
  if (hasHtml) {
    return <div className="news-article-body" dangerouslySetInnerHTML={{ __html: text }} />;
  }
  return text.split(/\n{2,}/).map((para, i) => (
    <p key={i} className="mb-4 last:mb-0 whitespace-pre-line">{para}</p>
  ));
};

const NewsDetailModal = ({ news, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!news) return null;

  const bodyText = news.content && String(news.content).trim() ? news.content : news.excerpt;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="news-detail-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#fdf9e7] border border-emerald-900/25 shadow-2xl"
        data-testid="news-detail-modal"
      >
        <button
          onClick={onClose}
          aria-label="Close article"
          className="absolute top-4 right-4 z-10 h-10 w-10 rounded-full bg-emerald-950/90 hover:bg-emerald-900 text-emerald-50 flex items-center justify-center transition"
          data-testid="news-detail-close"
        >
          <X size={18} />
        </button>

        {news.image && (
          <div className="aspect-[16/8] overflow-hidden rounded-t-2xl">
            <img src={news.image} alt={news.title} className="h-full w-full object-cover" />
          </div>
        )}

        <div className="p-6 md:p-9">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest">
            <span className="rounded-full bg-emerald-950 text-emerald-100 px-2.5 py-0.5">{news.category}</span>
            <span className="text-emerald-900/40">•</span>
            <span className="text-emerald-900/70">{news.date}</span>
            {news.featured && <span className="ml-2 text-amber-600 text-[11px]">★ Featured</span>}
          </div>

          <h1
            className="mt-4 font-serif text-emerald-950 text-3xl md:text-4xl leading-tight tracking-tight"
            data-testid="news-detail-title"
          >
            {news.title}
          </h1>

          <div
            className="mt-6 text-[15px] text-emerald-950/85 leading-[1.8] news-article-content"
            data-testid="news-detail-content"
          >
            {renderBody(bodyText)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsDetailModal;
